<template>
  <div class="bg-background-light dark:bg-background-dark text-text-main font-display antialiased min-h-screen flex flex-col">
    <AppHeader />
    <main class="flex-grow flex flex-col w-full max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 gap-6">
      <nav class="flex items-center text-sm text-text-muted">
        <router-link class="hover:text-primary transition-colors" to="/">首页</router-link>
        <span class="mx-2 text-gray-400">/</span>
        <span class="text-text-main font-medium dark:text-white">政策法规</span>
      </nav>

      <!-- Banner -->
      <div class="flex flex-col justify-end overflow-hidden rounded-xl min-h-[200px] lg:min-h-[280px] relative">
        <div class="absolute inset-0 bg-cover bg-center transition-all duration-1000"
             :class="imageLoadedMap['banner'] ? 'blur-0 scale-100' : 'blur-2xl scale-110 opacity-50'"
             style="background-image: linear-gradient(0deg, rgba(19, 91, 236, 0.8) 0%, rgba(19, 91, 236, 0.4) 100%), url('https://lh3.googleusercontent.com/aida-public/AB6AXuD-AESi1VEm6HGVcyogDnpGpydhkbXvNYCLODmIiXZOYyio5Q8TwtA8x6OHWRSHV9p83n-HDxxtFVCZxJwKclzqM3vvrjX6NoMjIxexetp7Px1mNyTnZ9YjNMZQxQtPNxayhh-7XBSgURgGXlJC989oCwtscJ3VS3vE_4pgm0bVsmooEKUkIjsfbyZDcsKlxdtPBRY3IrM6IffgJ8a5Fatdg6kFIQr3yYP9dCli9VNzxzEoP_pLopRQsZsJfmSiRcbphQXlJWLFLxY');">
        </div>
        <img src="https://lh3.googleusercontent.com/aida-public/AB6AXuD-AESi1VEm6HGVcyogDnpGpydhkbXvNYCLODmIiXZOYyio5Q8TwtA8x6OHWRSHV9p83n-HDxxtFVCZxJwKclzqM3vvrjX6NoMjIxexetp7Px1mNyTnZ9YjNMZQxQtPNxayhh-7XBSgURgGXlJC989oCwtscJ3VS3vE_4pgm0bVsmooEKUkIjsfbyZDcsKlxdtPBRY3IrM6IffgJ8a5Fatdg6kFIQr3yYP9dCli9VNzxzEoP_pLopRQsZsJfmSiRcbphQXlJWLFLxY" class="hidden" @load="onImageLoad('banner')" />
        <div class="absolute inset-0 bg-black/10 z-10"></div>
        <div class="flex flex-col p-6 lg:p-10 relative z-20">
          <h1 class="text-white tracking-tight text-3xl lg:text-4xl font-bold leading-tight mb-2">政策法规库</h1>
          <p class="text-white/80 text-base lg:text-lg font-medium max-w-2xl">
            汇集国家法律、省级法规、行业标准及协会章程，为建设行业提供权威的政策指导与查询服务。
          </p>
        </div>
      </div>

      <!-- Main Content Area with Year Filter Sidebar -->
      <div class="flex flex-col lg:flex-row gap-8 mt-2">
        <!-- Sidebar -->
        <aside class="w-full lg:w-64 flex-shrink-0 space-y-6">
          <div class="bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div class="p-4 border-b border-gray-100 dark:border-gray-800">
              <h3 class="font-bold text-text-main dark:text-white flex items-center gap-2">
                <span class="material-symbols-outlined text-primary">calendar_month</span>
                年份筛选
              </h3>
            </div>
            <div class="p-2 flex flex-col gap-1">
              <button 
                class="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded-lg transition-colors"
                :class="!queryParams.year ? 'bg-primary/10 text-primary' : 'text-text-main hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'"
                @click="filterByYear(null)">
                <span>全部年份</span>
                <span class="text-xs opacity-70">{{ total }}</span>
              </button>
              <button 
                v-for="(count, year) in yearCounts" 
                :key="year"
                class="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded-lg transition-colors"
                :class="queryParams.year == year ? 'bg-primary/10 text-primary' : 'text-text-main hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'"
                @click="filterByYear(year)">
                <span>{{ year }} 年</span>
                <span class="text-xs opacity-70">{{ count }}</span>
              </button>
            </div>
          </div>
        </aside>

        <!-- List Content -->
        <div class="flex-1 flex flex-col gap-6">
          <div v-if="loading" class="flex flex-col gap-6">
            <el-skeleton v-for="i in 3" :key="i" :rows="3" animated class="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-100 dark:border-gray-800" />
          </div>
          
          <div v-else-if="policies.length > 0" class="flex flex-col gap-6">
            <article v-for="item in policies" :key="item.id" 
                     class="group bg-surface-light dark:bg-surface-dark p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md hover:border-primary/30 transition-all duration-200 cursor-pointer"
                     @click="$router.push(`/policy/detail/${item.id}`)">
              <div class="flex flex-col sm:flex-row justify-between gap-4">
                <div class="flex-1">
                  <div class="flex items-start gap-2 mb-3">
                    <span v-if="isRecent(item.createTime)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">最新</span>
                  </div>
                  <h3 class="text-xl font-bold text-text-main dark:text-white mb-3 group-hover:text-primary transition-colors leading-snug">
                    {{ item.title }}
                  </h3>
                  <div class="flex items-center gap-6 text-xs text-text-muted">
                    <span class="flex items-center gap-1.5">
                      <span class="material-symbols-outlined text-[18px]">calendar_today</span>
                      发布日期：{{ formatDate(item.createTime) }}
                    </span>
                    <span class="flex items-center gap-1.5">
                      <span class="material-symbols-outlined text-[18px]">visibility</span>
                      阅读量：{{ item.viewCount || 0 }}
                    </span>
                  </div>
                </div>
                <div class="flex sm:flex-col justify-end gap-2 shrink-0">
                  <button class="flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-bold text-primary bg-primary/10 rounded-lg group-hover:bg-primary group-hover:text-white transition-all">
                    <span class="material-symbols-outlined text-[20px]">menu_book</span>
                    查阅详情
                  </button>
                </div>
              </div>
            </article>
          </div>

          <div v-else class="py-20 text-center text-gray-400">
            <span class="material-symbols-outlined text-6xl mb-4">description</span>
            <p class="text-lg">暂无政策法规数据</p>
          </div>

          <!-- Pagination -->
          <div v-if="total > queryParams.size" class="flex justify-center mt-6">
            <el-pagination
              v-model:current-page="queryParams.current"
              v-model:page-size="queryParams.size"
              :total="total"
              layout="prev, pager, next"
              @current-change="fetchPolicies"
              background
            />
          </div>
        </div>
      </div>
    </main>

    <footer class="bg-surface-light dark:bg-surface-dark border-t border-gray-200 dark:border-gray-800 mt-12">
      <div class="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center gap-3 mb-4 text-primary">
              <div class="flex-shrink-0">
                <img alt="Logo" class="w-10 h-10 rounded-lg shadow-sm" src="/logo.png"/>
              </div>
              <span class="font-bold text-xl text-text-main dark:text-white">湖北省建筑劳务协会</span>
            </div>
            <p class="text-text-muted text-sm leading-relaxed max-w-sm">
              弘扬工匠精神，助推行业发展!<br/>
              ——为建筑劳务保驾护航!
            </p>
          </div>
          <div>
            <h4 class="font-bold text-text-main dark:text-white mb-4">快速链接</h4>
            <ul class="space-y-2 text-sm text-text-muted">
              <li><a class="hover:text-primary transition-colors" href="https://zjt.hubei.gov.cn/" target="_blank">湖北省住建厅官网</a></li>
            </ul>
          </div>
          <div>
            <h4 class="font-bold text-text-main dark:text-white mb-4">联系我们</h4>
            <ul class="space-y-3 text-sm text-text-muted">
              <li class="flex items-start gap-2">
                <span class="material-symbols-outlined text-lg shrink-0 mt-0.5">location_on</span>
                <span>武汉市汉阳区龙阳大道123号<br/>欧亚达汉阳国际广场A座1803</span>
              </li>
              <li class="flex items-center gap-2">
                <span class="material-symbols-outlined text-lg shrink-0">call</span>
                <span>027-84881278</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="border-t border-gray-200 dark:border-gray-800 mt-12 pt-8 text-center text-xs text-text-muted">
          <p>©2018 湖北省建筑劳务协会保留所有权利。 | <a href="https://beian.miit.gov.cn/" target="_blank" class="hover:text-primary">鄂ICP备18023234号-1</a></p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import AppHeader from '@/components/AppHeader.vue';
import newsService from '@/api/newsService';
import dayjs from 'dayjs';

const loading = ref(true);
const policies = ref([]);
const total = ref(0);
const imageLoadedMap = reactive({});
const yearCounts = ref({});

const queryParams = reactive({
  current: 1,
  size: 10,
  year: null
});

const onImageLoad = (id) => {
  imageLoadedMap[id] = true;
};

const fetchYearCounts = async () => {
  try {
    const res = await newsService.getPolicyCountsByYear();
    if (res && res.data) {
      yearCounts.value = res.data;
    }
  } catch (error) {
    console.error('获取年份统计失败:', error);
  }
};

const fetchPolicies = async () => {
  loading.value = true;
  try {
    const res = await newsService.getPolicyPage(queryParams);
    if (res && res.data) {
      policies.value = res.data.content || [];
      total.value = res.data.totalElements || 0;
    }
  } catch (error) {
    console.error('获取政策法规失败:', error);
  } finally {
    loading.value = false;
  }
};

const filterByYear = (year) => {
  queryParams.year = year;
  queryParams.current = 1;
  fetchPolicies();
};

const formatDate = (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-';
const isRecent = (date) => {
  if (!date) return false;
  return dayjs().diff(dayjs(date), 'day') < 30;
};

onMounted(() => {
  fetchPolicies();
  fetchYearCounts();
});
</script>

<style scoped>
/* Scoped styles if any. Tailwind classes are mostly used. */
</style>
